import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import theme from '../../../constants/theme';
import { LINE_MID, resetButton } from './constants';

const QUIET_ZONE = 4;
const PNG_SIZE = 1024;
const MODULE_RADIUS = 0.25;
/** Badge radius as a fraction of the code — the design's r=11 on a 100 grid. */
const BADGE_RATIO = 0.11;

/** Finder patterns are the only modules we recolor. */
function isFinder(row: number, col: number, size: number) {
  return (
    (row < 7 && col < 7) ||
    (row < 7 && col >= size - 7) ||
    (row >= size - 7 && col < 7)
  );
}

async function buildSvg(url: string): Promise<string> {
  const QRCode = await import('qrcode');
  // High correction so the centre badge never costs us a scan.
  const { modules } = QRCode.create(url, { errorCorrectionLevel: 'H' });
  const { size, data } = modules;

  const total = size + QUIET_ZONE * 2;
  const centre = total / 2;
  const radius = size * BADGE_RATIO;
  // Clear slightly past the disc so no module touches its edge.
  const clearance = radius + 0.6;

  let dark = '';
  let brand = '';
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      if (!data[row * size + col]) continue;

      const x = col + QUIET_ZONE;
      const y = row + QUIET_ZONE;
      if (Math.hypot(x + 0.5 - centre, y + 0.5 - centre) < clearance) continue;

      const rect = `<rect x="${x}" y="${y}" width="1" height="1" rx="${MODULE_RADIUS}"/>`;
      if (isFinder(row, col, size)) brand += rect;
      else dark += rect;
    }
  }

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}">`,
    `<rect width="${total}" height="${total}" fill="#FFFFFF"/>`,
    `<g fill="#111111">${dark}</g>`,
    `<g fill="${theme.palette.primary.main}">${brand}</g>`,
    `<circle cx="${centre}" cy="${centre}" r="${radius}" fill="${theme.palette.primary.main}"/>`,
    `<text x="${centre}" y="${centre}" dy="0.35em" text-anchor="middle"`,
    ` font-family="Montserrat, Helvetica, Arial, sans-serif"`,
    ` font-size="${radius * 1.1}" font-weight="700" fill="#FFFFFF">P</text>`,
    '</svg>',
  ].join('');
}

function downloadPng(svg: string, fileName: string) {
  const image = new Image();
  image.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = PNG_SIZE;
    canvas.height = PNG_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(image, 0, 0, PNG_SIZE, PNG_SIZE);
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = fileName;
    link.click();
  };
  image.onerror = () => toast.error('Could not build your QR code.');
  image.src = `data:image/svg+xml;base64,${window.btoa(svg)}`;
}

interface Props {
  url: string;
}

/** Never themed — a QR code has to stay high-contrast to scan. */
function QrSection({ url }: Props) {
  const [svg, setSvg] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let active = true;
    buildSvg(url)
      .then(next => {
        if (active) setSvg(next);
      })
      .catch(() => setSvg(null));
    return () => {
      active = false;
    };
  }, [url]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Box
          sx={{
            width: 112,
            height: 112,
            flex: 'none',
            border: '1px solid #000',
            borderRadius: '10px',
            background: '#fff',
            overflow: 'hidden',
            '& svg': { width: '100%', height: '100%', display: 'block' },
          }}
          // Built from a QR matrix we generate — no external input reaches this.
          dangerouslySetInnerHTML={{ __html: svg || '' }}
        />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.45, color: 'text.secondary' }}>
            Points at your booking site. Print it for the shop counter or drop
            it in a story.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              typography: 'h5',
              fontWeight: 700,
            }}>
            <Box
              component="button"
              type="button"
              disabled={!svg}
              onClick={() => setIsOpen(true)}
              sx={{ ...resetButton, color: 'primary.main' }}>
              View
            </Box>
            <Box sx={{ color: LINE_MID }}>·</Box>
            <Box
              component="button"
              type="button"
              disabled={!svg}
              onClick={() => svg && downloadPng(svg, 'booking-site-qr.png')}
              sx={{ ...resetButton, color: 'primary.main' }}>
              Download
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        fullWidth
        maxWidth="xs">
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              width: '100%',
              aspectRatio: '1',
              '& svg': { width: '100%', height: '100%', display: 'block' },
            }}
            dangerouslySetInnerHTML={{ __html: svg || '' }}
          />
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: 'text.secondary' }}>
            {url.replace(/^https?:\/\//, '')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => svg && downloadPng(svg, 'booking-site-qr.png')}>
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default QrSection;
